import React from 'react';
import { Grid, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';

import TripPreCheckSettings from '../../trips/TripPreCheckSettings';
import { normalizePreCheckSettings } from '../../trips/preCheckSettings';
import { ContextStore } from '../../store/contextStore';
import { CREATE, DataAPIContext, GET_ONE, UPDATE } from '../../api';
import FormActionButtons from '../../components/FormActionButtons';
import FormLicenseeContext from '../../components/FormLicenseeContext';
import LicenseeFormField from '../../components/LicenseeFormField';
import PageHeading from '../../components/PageHeading';
import PageMeta from '../../components/PageMeta';
import { isOk, serverErrorMessage } from '../../utils';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(2),
        paddingBottom: 0,
        backgroundColor: '#FCFCFE',
        [theme.breakpoints.down('sm')]: {
            margin: theme.spacing(0),
        },
    },
    innerGridRoot: {
        overflowY: 'scroll',
        maxHeight: 'calc(100vh - 70px)',
        [theme.breakpoints.down('sm')]: {
            overflowY: 'scroll',
            maxHeight: 'calc(100vh - 130px)',
        },
    },
    buttonPanel: {
        backgroundColor: '#F5F5F5',
        zIndex: 10,
        [theme.breakpoints.down('sm')]: {
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            height: theme.spacing(8),
        },
    },
    progress: {
        marginLeft: theme.spacing(1),
    },
}));

const CheckinSettings = () => {
    const classes = useStyles();
    const dataAPI = React.useContext(DataAPIContext);
    const { tourOperator, hasPerm, updateTourOperator, isFranchise, currentLicensee } = React.useContext(ContextStore);
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    useMediaQuery(theme.breakpoints.up('md'));

    // In franchise mode, settings are per-licensee. Default to the globally selected licensee.
    const [selectedLicensee, setSelectedLicensee] = React.useState(currentLicensee || null);
    const effectiveTopId = (isFranchise && selectedLicensee) ? selectedLicensee : tourOperator.id;

    const [top, setTop] = React.useState(tourOperator);
    const [syncActive, setSyncActive] = React.useState(false);
    const [loadedSnapshot, setLoadedSnapshot] = React.useState(JSON.stringify({}));
    const hasWritePerm = hasPerm('tour_operator', 'write');

    React.useEffect(() => {
        if (isFranchise && !selectedLicensee) return;
        let active = true;
        setSyncActive(true);
        dataAPI(GET_ONE, 'tour_operator', { id: effectiveTopId, urlSuffix: 'checkin_settings/' })
            .then((res) => {
                if (!active) return;
                if (!isOk(res)) {
                    enqueueSnackbar(serverErrorMessage(res, 'Error loading settings'), { variant: 'error' });
                    return;
                }
                if (res.data) {
                    setTop((prev) => ({ ...prev, pre_check_settings: res.data }));
                    setLoadedSnapshot(JSON.stringify(res.data || {}));
                }
            })
            .finally(() => active && setSyncActive(false));
        return () => { active = false; };
    }, [dataAPI, effectiveTopId, enqueueSnackbar]);

    const onChange = (updated) => {
        // Support both object updates and functional updates from TripPreCheckSettings
        if (typeof updated === 'function') {
            setTop((prev) => {
                const prevTrip = { pre_check_settings: prev?.pre_check_settings || {} };
                const nextTrip = updated(prevTrip) || prevTrip;
                return { ...prev, pre_check_settings: nextTrip.pre_check_settings };
            });
        } else if (updated && typeof updated === 'object') {
            setTop((prev) => ({ ...prev, pre_check_settings: updated.pre_check_settings }));
        }
    };

    const onSave = () => {
        setSyncActive(true);
        dataAPI(UPDATE, 'tour_operator', { id: effectiveTopId, urlSuffix: 'checkin_settings/', data: top.pre_check_settings })
            .then((res) => {
                // A refused save resolves with the error body: keep the edits and say why
                if (!isOk(res)) {
                    enqueueSnackbar(serverErrorMessage(res, 'Error saving settings'), { variant: 'error' });
                    return;
                }
                enqueueSnackbar('Settings saved', { variant: 'success' });
                // Optionally refresh bootstrap copy
                if (effectiveTopId === tourOperator.id) {
                    updateTourOperator({ ...tourOperator });
                }
                // Reset dirty snapshot
                setLoadedSnapshot(JSON.stringify(top?.pre_check_settings || {}));
            })
            .catch(() => enqueueSnackbar('Error saving settings', { variant: 'error' }))
            .finally(() => setSyncActive(false));
    };

    // CP-7540: "Apply to all trips" turns "Use Tour Operator default settings" on for every trip on its own
    // pre-check-in settings (those the user's tour.write access tags reach). Only the count changes here, in
    // the snapshot too, so the page does not turn dirty.
    const canApplyToAllTrips = hasWritePerm && hasPerm('tour', 'write');
    const onApplyToAllTrips = () => dataAPI(CREATE, 'tour_operator', {
        urlSuffix: `${effectiveTopId}/checkin_settings/apply_to_all/`,
        data: {},
        // A 403 is "not your permission", not an expired session.
        keepSessionOn403: true,
    })
        .then((res) => {
            if (!isOk(res)) {
                enqueueSnackbar(serverErrorMessage(res, 'Could not apply the defaults to all trips'), { variant: 'error' });
                return;
            }
            const switched = res.data?.trips_switched_count ?? 0;
            const remaining = res.data?.trips_with_own_settings_count ?? 0;
            const withCount = (settings) => ({ ...settings, trips_with_own_settings_count: remaining });
            setTop((prev) => ({ ...prev, pre_check_settings: withCount(prev?.pre_check_settings || {}) }));
            setLoadedSnapshot((prev) => {
                try {
                    return JSON.stringify(withCount(JSON.parse(prev)));
                } catch (_e) {
                    return prev;
                }
            });
            let message = `${switched} trip${switched === 1 ? ' now uses' : 's now use'} these defaults.`;
            if (remaining > 0) {
                message += ` ${remaining} you cannot edit ${remaining === 1 ? 'keeps its' : 'keep their'} own settings.`;
            }
            enqueueSnackbar(message, { variant: 'success' });
        })
        .catch(() => enqueueSnackbar('Could not apply the defaults to all trips', { variant: 'error' }));

    // CP-8423: no list to return to, so CANCEL reverts to the last loaded/saved settings.
    const onCancel = () => {
        let snapshot = {};
        try {
            snapshot = JSON.parse(loadedSnapshot);
        } catch (_e) {
            return;
        }
        setTop((prev) => ({ ...prev, pre_check_settings: snapshot }));
    };

    const fakeTrip = React.useMemo(() => ({
        id: effectiveTopId,
        tour_operator: selectedLicensee || tourOperator.id,
        pre_check_settings: top?.pre_check_settings || {},
        use_operator_pre_check_settings: false,
    }), [effectiveTopId, selectedLicensee, tourOperator.id, top?.pre_check_settings]);

    // Compared as the editor fills them in: its first edit stores the filled-in copy, which never equals the
    // loaded JSON, so turning a switch off and on again left Save, Cancel and "Apply to all trips" as if edited.
    const isDirty = React.useMemo(() => {
        try {
            const filledIn = (settings) => JSON.stringify(normalizePreCheckSettings(settings || {}));
            return filledIn(top?.pre_check_settings) !== filledIn(JSON.parse(loadedSnapshot));
        } catch (_e) {
            return true;
        }
    }, [top?.pre_check_settings, loadedSnapshot]);

    return (
        <>
            <FormLicenseeContext.Provider value={effectiveTopId}>
            <div className={classes.root}>
                <PageMeta title={'Pre-check-in settings'} />
                <PageHeading sx={{ mb: 1.5 }}>Pre-check-in settings</PageHeading>
                <Grid container direction="row">
                    <Grid item xs={12} sm={12} container direction="row" spacing={2} className={classes.innerGridRoot}>
                        {isFranchise && (
                            <Grid item xs={12} sm={6}>
                                <LicenseeFormField
                                    value={selectedLicensee}
                                    onChange={(id) => setSelectedLicensee(id)}
                                    error={!selectedLicensee}
                                    helperText={!selectedLicensee ? 'Please select a licensee' : undefined}
                                />
                            </Grid>
                        )}

                        {(!isFranchise || selectedLicensee) && <Grid item xs={12}>
                            <TripPreCheckSettings
                                data={fakeTrip}
                                onChange={(updatedTrip) => onChange(updatedTrip)}
                                hasWritePerm={hasWritePerm}
                                onSave={onSave}
                                mode="operator"
                                onApplyToAllTrips={canApplyToAllTrips ? onApplyToAllTrips : undefined}
                                applyToAllTripsBlocked={isDirty ? 'Save your changes first' : undefined}
                            />
                        </Grid>}
                    </Grid>
                </Grid>
            </div>

            <Grid item xs={12} className={classes.buttonPanel} container justifyContent="flex-end" alignItems="center" py={2} px={2}>
                <FormActionButtons
                    mode="edit"
                    size="medium"
                    onSubmit={onSave}
                    submitDisabled={syncActive || !hasWritePerm || !isDirty || (isFranchise && !selectedLicensee)}
                    submitLabel={<>Save{syncActive && <CircularProgress size={20} className={classes.progress} />}</>}
                    onCancel={onCancel}
                    cancelDisabled={syncActive || !isDirty}
                />
            </Grid>
            </FormLicenseeContext.Provider>
        </>
    );
};

export default CheckinSettings;


